import React, {Fragment, useEffect, useRef, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from './message-list';
import ProductImage from './ProductImage.jsx';
import Device from '@core/scripts/components/device';
import Landmark from '@core/scripts/components/landmark-adapter';
import Messages from '@core/scripts/components/messages';
import {getBestImageIndex, isLazyLoaded} from '../helper/image-slider-utils';
import AppGallery from './actions/app-gallery';
import ProductImageWishlistHeart from './ProductImageWishlistHeart.jsx';
import {yieldToMain} from "../helper/ProductPageHelper.jsx";
import ProductVideo from "./ProductVideo.jsx";
import {Carousel, Icon, Grid} from "@bonprix/design-system";
import ProductImageWithZoom from "./product-image/ProductImageWithZoom.jsx";
import ModelContainer from '@core/scripts/components/model-container';

const IMAGE_WIDTH = 100;

let indicesOfLoadedImages = [];

const isLivingAreaAssortment = ModelContainer.get('isLivingAreaAssortment');

export default function ProductImageSlider({product, texts, initialSlideIndex, productImageDimensions,onStyleChange}) {

    const [isButtonClickIgnored, setIsButtonClickIgnored] = useState(false);
    const [landscapeMode, setLandscapeMode] = useState(Device.isLandscapeMode());

    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

    const [currentItem, setCurrentItem] = useState(product.currentItem);
    const [currentItemOption, setCurrentItemOption] = useState(product.currentItemOption);
    const currentInitialSlideIndex = currentItem.video && initialSlideIndex > 0 ? initialSlideIndex + 1 : initialSlideIndex;
    const [slideIndex, setSlideIndex] = useState(currentInitialSlideIndex);
    const [videoState, setVideoState] = useState('stop');

    const transitionEnabled = useRef(true);


    useEffect(() => {
        AppGallery.addOnClosedListener((newSlideIndex) => {
            if (slideIndex !== newSlideIndex) {
                transitionEnabled.current = false;
                setSlideIndex(newSlideIndex);
            }
        });
        const subscriptions = [
            MessageBroker.subscribe(ProductMessages.CHANGED, ({item, itemOption}) => {
                setCurrentItemOption(itemOption);
                if (item !== undefined) {
                    setCurrentItem(item);
                    indicesOfLoadedImages = [];

                    if (!isSlideIndexValid(slideIndex, item)) {
                        transitionEnabled.current = false;
                        setSlideIndex(getBestImageIndex(item));
                    }
                }
            }),
            MessageBroker.subscribe(Messages.WINDOW.ORIENTATION_CHANGED, () => {
                setLandscapeMode(Device.isLandscapeMode());
            }),
            MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, ({majorBreakpointChanged}) => {
                if (majorBreakpointChanged) {
                    setLandscapeMode(Device.isLandscapeMode());
                }
            }),
        ];

        return () => subscriptions.forEach((subscription) => MessageBroker.unsubscribe(subscription));
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!transitionEnabled.current) {
            //enable button click
            setIsButtonClickIgnored(false);
            //reactivate transition after reinitialization of image loop
            if (slideIndex !== -1 && slideIndex !== images.length && images.length !== 1) {
                transitionEnabled.current = true;
            }
        }
        if (currentItem.video) {
            handleVideo(slideIndex);
        }
    }, [slideIndex]);

    const handleVideo = (index) => {
        if (index === 1) {
            setVideoState('play');
        } else {
            setVideoState('stop');
        }
    };

    const isSlideIndexValid = (slideIndex, item) => {
        return slideIndex >= 0 && slideIndex < item.images?.length;
    };


    const slideToSpecificImage = (index) => {
        yieldToMain(() => {
            setIsButtonClickIgnored(true);
            setSlideIndex(index);
            Landmark.sendClickLandmark(`slide\\${index + 1}`);
        });
    };

    const findNewImageIndex = (isSlideBack) => {
        return isSlideBack ? slideIndex - 1 : slideIndex + 1;
    };


    const shouldBeLazyLoaded = (index) => {
        const lazy = isLazyLoaded(index, images, slideIndex, indicesOfLoadedImages);
        if (!lazy && !indicesOfLoadedImages.includes(index)) {
            indicesOfLoadedImages.push(index);
        }

        return lazy;
    };

    const addVideoToSliderImagesIfAvailable = (images, video) => {
        if (video && !images.some(image => image.type === 'VIDEO')) {
            video.type = 'VIDEO';
            images.splice(1, 0, video);
        }
    };

    const {images, video, itemNumber} = currentItem;
    addVideoToSliderImagesIfAvailable(images, video);
    const firstImage = images[0];
    const soldOut = currentItem?.availability?.soldOut;
    const imageZoomAllowed = currentItem.imageZoomAllowed && !soldOut;

    const isArticleSoldOutOrHaveOneImage = images.length===1 || soldOut;

    useEffect(() => {
        onStyleChange(isArticleSoldOutOrHaveOneImage || isLivingAreaAssortment);
    }, [isArticleSoldOutOrHaveOneImage, isLivingAreaAssortment]);

    if (isArticleSoldOutOrHaveOneImage) {
        return (
            <div className="image__container_soldout__or__have_OneImage">
                <ProductImage
                    image={firstImage}
                    images={images}
                    zoomAllowed={imageZoomAllowed}
                    lazy={false}
                    soldOut={soldOut}
                    slideIndex={slideIndex}
                    texts={texts}
                    dimensions={productImageDimensions}
                />
            </div>
        );
    }

    function renderImageSliderWithFullWith() {
        return (
            <div className="image__container_soldout__or__have_OneImage">
                <Carousel
                    arrows
                    gap={0}
                    label="label"
                    onMove={() => {
                    }}
                    pagination
                    perPage={1}
                    rewind
                >
                    {images.map((image, index) => {
                        if (image.type === 'VIDEO') {
                            return (
                                <Carousel.Slide key={`video-${index}`}>
                                    <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                </Carousel.Slide>
                            )
                        }
                        return (
                            <Carousel.Slide key={`image-${index}`}>
                                <ProductImageWithZoom
                                    key={index}
                                    image={image}
                                    soldOut={soldOut}
                                    texts={texts}
                                    imageZoomAllowed={imageZoomAllowed}
                                />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            </div>
        )
    }

    return (
        <div>
            {isMobileView ? (
                <>
                    <div className="image__container">
                        <Carousel
                            arrows
                            gap={0}
                            label="label"
                            onMove={(index) => setSlideIndex(index)}
                            pagination
                            perPage={1}
                            rewind
                        >
                            {images.map((image, index) => {
                                if (image.type === 'VIDEO') {
                                    return (
                                        <Carousel.Slide key={currentItem.id +"-"+ index}>
                                            <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                        </Carousel.Slide>
                                    )
                                }
                                return (
                                    <Carousel.Slide key={currentItem.id +"-"+ index}>
                                        <ProductImage
                                            image={image}
                                            images={images}
                                            zoomAllowed={imageZoomAllowed}
                                            lazy={shouldBeLazyLoaded(index)}
                                            slideIndex={slideIndex}
                                            dimensions={productImageDimensions}
                                            index={index}/>
                                    </Carousel.Slide>
                                );
                            })}
                        </Carousel>
                        <div className="flex absolute top-8 right-8 w-[50px] h-[50px] items-center justify-center group is-mouse:cursor-pointer">
                            <ProductImageWishlistHeart
                                product={product}
                                currentItem={currentItem}
                                currentItemOption={currentItemOption}
                            />
                        </div>
                    </div>
                </>

            ) : (
                !isLivingAreaAssortment ? (
                    <Grid disableGutters>
                        {images.map((image, index) => {
                            if (image.type === 'VIDEO') {
                                return (
                                    <Grid.Item s={12} xxl={6}>
                                        <div className="product-image" key={index} style={{width: IMAGE_WIDTH + '%'}}>
                                            <ProductVideo video={video} state={videoState} itemNumber={itemNumber}/>
                                        </div>
                                    </Grid.Item>
                                );
                            }
                            return (
                                <Grid.Item s={12} xxl={6}>
                                    <ProductImageWithZoom
                                        key={index}
                                        image={image}
                                        soldOut={soldOut}
                                        texts={texts}
                                        imageZoomAllowed={imageZoomAllowed}
                                    />
                                </Grid.Item>
                            );
                        })}
                    </Grid>
                ) : (
                    renderImageSliderWithFullWith()
                )
            )}
        </div>
    )

}
